import { PAGINAS_DISPONIBLES } from './permisos-paginas2';

interface PermissionPageDefinition {
  ruta: string;
  nombre: string;
}

interface PermissionGroupDefinition {
  categoria: string;
  paginas: PermissionPageDefinition[];
}

/**
 * Permisos de página para los módulos operativos que fueron incorporados
 * después del catálogo histórico de permisos. Se registran sobre
 * PAGINAS_DISPONIBLES durante el arranque para que Configuración > Permisos
 * los muestre dentro del mismo árbol, sin duplicar categorías.
 */
export const PAGINAS_OPERATIVAS: PermissionGroupDefinition[] = [
  {
    categoria: 'Mantenimientos',
    paginas: [
      {
        ruta: 'mantenimientos/mantenimientos-km-hr/vigencias',
        nombre: '- Vigencias de equipos',
      },
      {
        ruta: 'mantenimientos/repositorio-documental',
        nombre: '- Repositorio documental',
      },
      {
        ruta: 'mantenimientos/repositorio-documental/vehiculos',
        nombre: '- Repositorio documental · Vehículos',
      },
      {
        ruta: 'mantenimientos/repositorio-documental/tipos-documentales',
        nombre: '- Repositorio documental · Tipos documentales',
      },
    ],
  },
  {
    categoria: 'Recursos Humanos',
    paginas: [
      {
        ruta: 'recursos-humanos/drive-documentos',
        nombre: '- Digitación desde Google Drive',
      },
      {
        ruta: 'recursos-humanos/contratacion/proceso-simple',
        nombre: '- Proceso simple de contratación',
      },
      {
        ruta: 'recursos-humanos/contratacion/dashboard/abastecimiento-gerencial',
        nombre: '- Abastecimiento Gerencial',
      },
    ],
  },
  {
    categoria: 'Formularios',
    paginas: [
      {
        ruta: 'formularios/contratacion/proyectos',
        nombre: '- Contratación - Por proyecto',
      },
      {
        ruta: 'formularios/contratacion/cargos',
        nombre: '- Contratación - Por cargo',
      },
      {
        ruta: 'formularios/contratacion/personal',
        nombre: '- Contratación - Personal',
      },
      {
        ruta: 'formularios/hospedaje',
        nombre: '- Solicitud de hospedaje',
      },
      {
        ruta: 'servicios/hospedaje/solicitudes-ver-todas',
        nombre: '- Hospedaje · Ver solicitudes de todos los usuarios',
      },
    ],
  },
  {
    categoria: 'Alimentación',
    paginas: [
      {
        ruta: 'alimentacion/reservar',
        nombre: '- Reservar alimentación',
      },
      {
        ruta: 'alimentacion/reservar-terceros',
        nombre: '- Reservar para terceros e invitados',
      },
      {
        ruta: 'alimentacion/gestion',
        nombre: '- Gestión de restaurantes',
      },
      {
        ruta: 'alimentacion/reportes',
        nombre: '- Reportes y conciliación',
      },
    ],
  },
  {
    categoria: 'SSA',
    paginas: [
      { ruta: 'ssa', nombre: 'SSA' },
      { ruta: 'ssa/control-seguridad', nombre: '- Control de Seguridad' },
      {
        ruta: 'ssa/control-seguridad/distribucion-diaria',
        nombre: '- Control de Seguridad · Distribución diaria de personal',
      },
      {
        ruta: 'ssa/control-seguridad/inicio-seguro',
        nombre: '- Control de Seguridad · Inicio Seguro GO / NO-GO',
      },
      {
        ruta: 'ssa/control-seguridad/evidencias-documentales',
        nombre: '- Control de Seguridad · Evidencias documentales diarias',
      },
      {
        ruta: 'ssa/control-seguridad/gestion-diaria',
        nombre: '- Control de Seguridad · Gestión diaria del Supervisor',
      },
      {
        ruta: 'ssa/control-seguridad/registro',
        nombre: '- Control de Seguridad · Registro diario',
      },
      {
        ruta: 'ssa/control-seguridad/iperc',
        nombre: '- Control de Seguridad · Matrices IPERC',
      },
      {
        ruta: 'ssa/control-seguridad/dashboard',
        nombre: '- Control de Seguridad · Dashboard',
      },
      {
        ruta: 'ssa/control-seguridad/charlas',
        nombre: '- Control de Seguridad · Gestión de charlas',
      },
      {
        ruta: 'ssa/control-seguridad/responsables',
        nombre: '- Control de Seguridad · Responsables',
      },
      {
        ruta: 'ssa/control-seguridad/configuracion',
        nombre: '- Control de Seguridad · Configuración y catálogos',
      },
      { ruta: 'ssa/epp', nombre: '- Gestión de EPP' },
      { ruta: 'ssa/productos-epp', nombre: '- Productos EPP' },
      { ruta: 'ssa/polizas', nombre: '- Gestión de pólizas' },
      {
        ruta: 'ssa/examenes-medicos',
        nombre: '- Exámenes y citas médicas',
      },
      { ruta: 'ssa/clinicas', nombre: '- Clínicas' },
      { ruta: 'ssa/vacunas', nombre: '- Vacunas' },
      {
        ruta: 'ssa/certificados-medicos',
        nombre: '- Certificados médicos',
      },
    ],
  },
  {
    categoria: 'Servicios',
    paginas: [
      { ruta: 'servicios', nombre: 'Servicios' },
      {
        ruta: 'servicios/logistica-personal',
        nombre: '- Logística de personal',
      },
      { ruta: 'servicios/pasajes', nombre: '- Gestión de pasajes' },
      { ruta: 'servicios/hospedaje', nombre: '- Gestión de hospedaje' },
      {
        ruta: 'servicios/hospedaje/infraestructura',
        nombre: '- Hospedaje · Habitaciones y camas',
      },
      {
        ruta: 'servicios/hospedaje/personal',
        nombre: '- Hospedaje · Gestión de personal',
      },
      {
        ruta: 'servicios/hospedaje/personal-crear',
        nombre: '- Hospedaje · Crear usuario y empleado',
      },
      {
        ruta: 'servicios/hospedaje/reportes',
        nombre: '- Hospedaje · Reportes de capacidad',
      },
      {
        ruta: 'servicios/hospedaje/solicitudes-crear',
        nombre: '- Hospedaje · Crear solicitudes',
      },
    ],
  },
  {
    categoria: 'ITE',
    paginas: [
      { ruta: 'ite', nombre: 'ITE' },
      {
        ruta: 'ite/ingresos-computadoras',
        nombre: '- Gestión de computadoras',
      },
      {
        ruta: 'ite/correos-corporativos',
        nombre: '- Correos corporativos',
      },
    ],
  },
  {
    categoria: 'Administración de Obras',
    paginas: [
      { ruta: 'administracion-obras', nombre: 'Administración de Obras' },
      {
        ruta: 'administracion-obras/distribucion-diaria',
        nombre: '- Matriz diaria de personal',
      },
      {
        ruta: 'administracion-obras/nuevos-ingresos',
        nombre: '- Nuevos ingresos',
      },
      { ruta: 'administracion-obras/personal', nombre: '- Personal' },
      { ruta: 'administracion-obras/asistencia', nombre: '- Asistencia' },
      {
        ruta: 'administracion-obras/eventos-internos',
        nombre: '- Eventos internos',
      },
      {
        ruta: 'administracion-obras/entradas-salidas',
        nombre: '- Entradas y salidas',
      },
      { ruta: 'administracion-obras/pasajes', nombre: '- Pasajes' },
      {
        ruta: 'administracion-obras/desvinculacion',
        nombre: '- Desvinculación',
      },
    ],
  },
  {
    categoria: 'Control de Proyectos',
    paginas: [
      {
        ruta: 'project-control/dashboard/financial',
        nombre: '- Dashboard · Resumen financiero y gráficas',
      },
      {
        ruta: 'project-control/dashboard/operative',
        nombre: '- Dashboard · Seguimiento operativo',
      },
    ],
  },
  {
    categoria: 'Compras',
    paginas: [
      {
        ruta: 'productos/compras/asignar-prioridad',
        nombre: '- Asignar prioridad P1–P4 y origen a productos sin clasificar',
      },
      {
        ruta: 'productos/compras/modificar-prioridad',
        nombre: '- Modificar prioridad/origen/proveedor ya asignados',
      },
      {
        ruta: 'productos/compras/liberar-reserva-ajena',
        nombre: '- Liberar reservas asignadas a otros compradores',
      },
      {
        ruta: 'productos/compras/cambiar-estado',
        nombre: '- Cambiar manualmente el estado de un ítem de compras',
      },
    ],
  },
];

const LEGACY_PERMISSION_PAGES_HIDDEN_FROM_SELECTOR = new Set([
  'ssa/capacitaciones',
  'ssa/materiales',
  // Se mantiene válido para usuarios actuales, pero ya no se ofrece como permiso nuevo.
  'project-control/dashboard',
]);

export function registerOperationalPermissionPages(): void {
  const catalog = PAGINAS_DISPONIBLES as PermissionGroupDefinition[];

  catalog.forEach((group) => {
    group.paginas = group.paginas.filter(
      (page) => !LEGACY_PERMISSION_PAGES_HIDDEN_FROM_SELECTOR.has(page.ruta),
    );
  });

  PAGINAS_OPERATIVAS.forEach((group) => {
    let destination = catalog.find(
      (item) => item.categoria === group.categoria,
    );

    if (!destination) {
      destination = {
        categoria: group.categoria,
        paginas: [],
      };
      catalog.push(destination);
    }

    group.paginas.forEach((page) => {
      const existing = destination!.paginas.find(
        (item) => item.ruta === page.ruta,
      );

      if (existing) {
        existing.nombre = page.nombre;
        return;
      }

      destination!.paginas.push({ ...page });
    });
  });
}
